/**
 * flushPendingFundingSaves — carry a save made while logged out through
 * registration, so it lands in user_watchlist instead of being lost.
 *
 * The static /funding pages cannot write to the database for a visitor who has
 * no session, so a Save there goes to localStorage under "mf_pending_saves" —
 * the same stash-then-flush shape the front door already uses for
 * "mf_pending_intent". This is the flush half, and it runs once, after a session
 * exists.
 *
 * It is deliberately forgiving: a save that fails is left in the stash for the
 * next attempt rather than dropped, and the whole thing is best-effort so a
 * network failure can never block somebody getting into their account.
 */
const KEY = "mf_pending_saves";
const ENDPOINT =
  "https://wnzxffwiiubqvcvnnjbz.supabase.co/functions/v1/run-service/funding_profile";

// action "watch" (2026-09-25): the "Keep an eye on them" button on a funding
// company page, clicked while logged out. It is flushed as funding_profile's
// watch (user_watchlist, entity_type 'company', entity_key = the page slug) so
// the company is already on the watchlist when the visitor lands. Anything else
// is the page bookmark, as before.
type PendingSave = { key?: string; slug?: string; url?: string; name?: string; at?: number; action?: "save" | "watch" };

function read(): PendingSave[] {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : null;
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export async function flushPendingFundingSaves(accessToken: string | null | undefined): Promise<number> {
  if (!accessToken) return 0;
  const items = read().filter((x) => x && typeof x.slug === "string" && x.slug);
  if (!items.length) return 0;

  const failed: PendingSave[] = [];
  let saved = 0;
  for (const it of items) {
    try {
      const r = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({
          slug: it.slug,
          action: it.action === "watch" ? "watch" : "save",
          save_key: it.key || it.slug,
          save_url: it.url || undefined,
        }),
      });
      const j = await r.json().catch(() => null);
      if (j && j.ok) saved++; else failed.push(it);
    } catch {
      failed.push(it);
    }
  }

  try {
    // Anything that did not land stays for the next sign-in. Clearing the lot on
    // a partial failure is how a save silently disappears.
    if (failed.length) localStorage.setItem(KEY, JSON.stringify(failed));
    else localStorage.removeItem(KEY);
  } catch { /* a storage failure is not worth blocking a login over */ }

  // LEAVE A NOTE FOR THE PAGE THEY CAME FROM. The save lands here, inside the
  // app, but the visitor is about to be returned to a static funding page that
  // has no way of knowing this ran. Without the note they arrive back at a page
  // that looks exactly as it did before they registered, and have to take it on
  // faith. The static page reads this once and says so, then clears it.
  if (saved > 0) {
    try {
      localStorage.setItem("mf_saves_flushed", JSON.stringify({ n: saved, at: Date.now() }));
    } catch { /* same reasoning as above */ }
  }

  return saved;
}
